<script setup lang="ts">
import { z } from 'zod'
import { isErrorWithMessage } from '../../../../../nuxt_shared/app/types/guards'
import { UiAlert } from '#components'
import refreshAll from '~/services/refreshHelper'

const { getLabel: tournamentTypeLabel } = useEnumTournamentType()
const { getLabel: genderGroupLabel } = useEnumGenderGroup()
const { $api } = useNuxtApp()
const { t } = useI18n()
const authStore = useAuthStore()
const genderGroup = useEnumGenderGroup()

const { tournament } = defineProps<{
  tournament: Tournament
}>()

const imgopen = ref(false)

// Component logic
const loadingRegister = ref(false)

async function cancelRegistration() {
  loadingRegister.value = true
  if (!authStore.user) {
    throw new Error('Not logged in')
  }

  try {
    await $api('/tournaments/{tournament}/unregister', {
      method: 'post',
      path: {
        tournament: tournament.id,
      },
    })
  }
  catch (error) {
    if (isErrorWithMessage(error)) {
      useSonner.error(t('Registration could not be cancelled: ') + error.data.message)
    }
  }

  await refreshAll(() => {})
  loadingRegister.value = false
}

async function register() {
  loadingRegister.value = true
  if (!authStore.user) {
    throw new Error('Not logged in')
  }

  try {
    await $api('/tournaments/{tournament}/register', {
      method: 'post',
      path: {
        tournament: tournament.id,
      },
    })
  }
  catch (error) {
    if (isErrorWithMessage(error)) {
      useSonner.error(t('Registration failed: ') + error.data.message)
    }
  }

  await refreshAll(() => {})
  loadingRegister.value = false
}

const googleSrc = `https://www.google.com/maps/embed/v1/place?key=AIzaSyBNYVW95M9oWQemV3RVZ8KuQDEpNVa1NME&q=${tournament.address_simple}`

const { handleSubmit, resetForm, values } = useForm({
  validationSchema: toTypedSchema(z.discriminatedUnion('partner_has_darts_space', [
    z.object({
      partner_has_darts_space: z.literal(true),
      email: z.string().email(),
    }),
    z.object({
      partner_has_darts_space: z.literal(false),
      first_name: z.string().min(2),
      last_name: z.string().min(2),
      gender: z.enum(['male', 'female', 'other']),
    }),
  ])),
  initialValues: {
    partner_has_darts_space: false,
    first_name: '',
    last_name: '',
    gender: 'other',
    // @ts-expect-error - email is not required
    email: '',
  },
})

const registerCouple = handleSubmit((data) => {
  loadingRegister.value = true
  $api('/tournaments/{tournament}/register-couple', {
    method: 'post',
    path: {
      tournament: tournament.id,
    },
    body: {
      ...data,
    },
  }).then(() => {
    refreshAll(() => {})
    resetForm()
  }).catch((error) => {
    if (isErrorWithMessage(error)) {
      useSonner.error(t('Registration failed: ') + error.data.message)
    }
  }).finally(() => {
    loadingRegister.value = false
  })
})
</script>

<template>
  <div class="flex flex-col gap-4">
    <UiAlert
      v-if="tournament.registration_possible === 'yes' || tournament.registration_possible === 'waiting_list'"
      variant="success"
      :title="$t('Registrations are open')"
    >
      <template #description>
        {{ $t('You can register for this tournament using dartsspace until: ') }}
        <strong>
          <CommonDateTime :date="tournament.registration_deadline" />
        </strong>
      </template>
    </UiAlert>
    <div class="grid lg:grid-cols-2 gap-4">
      <UiCard :title="$t('Info')">
        <template #description>
          {{ (('Starts at:')) }}
          <CommonDateTime :date="tournament.starts_at" />
        </template>
        <template #actions>
          <CommonShareButtons :url="'https://dartsspace.com' + $localePath(`/tournaments/${tournament.id}`)" />
        </template>
        <template #content>
          <UiCardContent class="flex flex-col gap-2">
            <div>
              <TournamentStatusBadge :tournament="tournament" />
            </div>
            <UiAlert
              v-if="tournament.manual_knockout && !tournament.with_group_phase"
              variant="warning"
              :title="$t('This tournament is using a manual knockout setup.')"
              :description="$t('Draw was not generated in random order.')"
            />
            <UiDescriptionList>
              <!-- Description -->
              <template v-if="tournament.description">
                <UiDescriptionListTerm>
                  {{ $t('Description') }}
                </UiDescriptionListTerm>
                <UiDescriptionListDetails>
                  {{ tournament.description }}
                </UiDescriptionListDetails>
              </template>
              <!-- Type -->
              <UiDescriptionListTerm>
                {{ $t('Type') }}
              </UiDescriptionListTerm>
              <UiDescriptionListDetails>
                {{ tournamentTypeLabel(tournament.type) }}
              </UiDescriptionListDetails>
              <!-- Start -->
              <UiDescriptionListTerm>
                {{ $t('Starts at') }}
              </UiDescriptionListTerm>
              <UiDescriptionListDetails>
                <CommonDateTime :date="tournament.starts_at" />
              </UiDescriptionListDetails>
              <!-- Gender -->
              <UiDescriptionListTerm>
                {{ $t('Gender') }}
              </UiDescriptionListTerm>
              <UiDescriptionListDetails>
                {{ genderGroupLabel(tournament.gender) }}
              </UiDescriptionListDetails>
              <!-- Price -->
              <UiDescriptionListTerm>
                {{ $t('Price') }}
              </UiDescriptionListTerm>
              <UiDescriptionListDetails>
                {{ tournament.display_price }}
              </UiDescriptionListDetails>
            </UiDescriptionList>
            <div
              v-if="!tournament.complete && tournament.registration_status === 'not_registered'"
              class="flex flex-col gap-2"
            >
              <UiAlert
                v-if="tournament.registration_possible === 'waiting_list'"
                variant="success"
                :title="$t('Tournament is full, you can register on the waiting list')"
              />
              <UiAlert
                v-if="tournament.registration_possible === 'full'"
                variant="success"
                :title="$t('Tournament is full, registrations are closed')"
              />
              <UiAlert
                v-if="tournament.registration_possible === 'closed'"
                variant="warning"
                :title="$t('Registrations are closed')"
              />
              <UiAlert
                v-if="tournament.registration_possible === 'no'"
                variant="warning"
                :title="$t('This tournament does not allow online registrations')"
              />
            </div>
            <UiAlert
              v-if="tournament.registration_status === 'registered' && !tournament.use_couples_registration"
              variant="success"
              :title="$t('You are registered for this tournament')"
            />
            <UiAlert
              v-if="tournament.registration_status === 'registered' && tournament.use_couples_registration"
              variant="success"
              :title="$t('You and your partner are registered for this tournament')"
              :description="$t('Your partner is: ') + tournament.registration_partner"
            />
            <UiAlert
              v-if="tournament.registration_status === 'on_waiting_list'"
              variant="warning"
              :title="$t('You are on the waiting list for this tournament')"
            />
            <template v-if="authStore.user && (tournament.registration_possible === 'yes' || tournament.registration_possible === 'waiting_list') && tournament.registration_status === 'not_registered'">
              <template v-if="tournament.use_couples_registration">
                <h2 class="text-xl">
                  {{ $t('Who are you playing with?') }}
                </h2>
                <UiFormSimple
                  @submit.prevent="registerCouple"
                >
                  <div class="flex flex-row gap-2 items-center">
                    <UiSwitchFullSwitch
                      :label="$t('My partner has a DartsSpace account')"
                      name="partner_has_darts_space"
                    />
                  </div>

                  <UiFullInput
                    v-if="values.partner_has_darts_space"
                    name="email"
                    :label="$t('E-mail')"
                    :description="$t('You can only enter an e-mail tied to a DartsSpace account, ask your partner to create an account if they want to have this tournament tied to their account.')"
                  />
                  <div class="flex flex-col md:grid md:grid-cols-2 gap-2 w-full">
                    <UiFullInput
                      v-if="!values.partner_has_darts_space"
                      name="first_name"
                      :label="$t('First name')"
                    />
                    <UiFullInput
                      v-if="!values.partner_has_darts_space"
                      name="last_name"
                      :label="$t('Last name')"
                    />
                  </div>
                  <UiFullSelect
                    v-if="!values.partner_has_darts_space"
                    name="gender"
                    :label="$t('Gender')"
                  >
                    <UiSelectItem
                      v-for="option in genderGroup.gendersAsOptions(false)"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </UiSelectItem>
                  </UiFullSelect>
                  <UiButton
                    variant="success"
                    :loading="loadingRegister"
                  >
                    <template v-if="tournament.registration_possible === 'waiting_list'">
                      {{ $t('Register us on waiting list') }}
                    </template>
                    <template v-else>
                      {{ $t('Register us for this tournament') }}
                    </template>
                  </UiButton>
                </UiFormSimple>
              </template>
              <template v-else>
                <UiButton
                  variant="success"
                  :loading="loadingRegister"
                  @click="register"
                >
                  <template v-if="tournament.registration_possible === 'waiting_list'">
                    {{ $t('Register on waiting list') }}
                  </template>
                  <template v-else>
                    {{ $t('Register for this tournament') }}
                  </template>
                </UiButton>
              </template>
            </template>
            <template v-else-if="(tournament.registration_status === 'registered' || tournament.registration_status === 'on_waiting_list') && !tournament.closed">
              <UiButton
                variant="destructive"
                :loading="loadingRegister"
                @click="cancelRegistration"
              >
                <template v-if="tournament.registration_status === 'on_waiting_list'">
                  {{ $t('Remove me from the waiting list') }}
                </template>
                <template v-else>
                  <template v-if="tournament.use_couples_registration">
                    {{ $t('Cancel our registration') }}
                  </template>
                  <template v-else>
                    {{ $t('Cancel my registration') }}
                  </template>
                </template>
              </UiButton>
            </template>
            <template v-else-if="tournament.registration_possible === 'yes' || tournament.registration_possible === 'waiting_list'">
              <UiAlert
                icon="i-tabler-lock"
                variant="default"
                filled
                :title="$t('Login required')"
                :actions="[{ label: $t('Login or create account'), to: $localePath('/auth/login') }]"
              >
                {{
                  $t('You need to be logged in to register for this tournament. Please login or create an account.')
                }}
              </UiAlert>
            </template>
          </UiCardContent>
          <UiCardFooter>
            <UiButton
              v-if="tournament.event_id"
              :to="$localePath(`/events/${tournament.event_id}`)"
              color="primary"
              size="sm"
            >
              <template v-if="tournament.event_type === 'darts_location'">
                {{ $t('View all tournaments at this location') }}
              </template>
              <template v-else>
                {{ $t('View all tournaments at this event') }}
              </template>
            </UiButton>
          </UiCardFooter>
        </template>
      </UiCard>
      <UiCard
        v-if="tournament.image"
        class="flex items-center justify-center"
      >
        <UiDialog v-model:open="imgopen">
          <UiDialogTrigger as-child>
            <NuxtImg
              v-if="tournament.image"
              :src="tournament.image.replace('dartsspace.test', 'api.dartsspace.com')"
              fit="contain"
              quality="80"
              loading="lazy"
              class="rounded-lg max-h-[300px] cursor-pointer"
              height="300"
              @click="imgopen = true"
            />
          </UiDialogTrigger>
          <UiDialogContent>
            <NuxtImg
              v-if="tournament.image"
              :src="tournament.image.replace('dartsspace.test', 'api.dartsspace.com')"
              fit="contain"
              quality="80"
              loading="lazy"
              class="rounded-lg"
              height="600"
              @click="imgopen = true"
            />
          </UiDialogContent>
        </UiDialog>
      </UiCard>

      <LazyVisibleTournamentParticipantListingInformation
        v-if="tournament.player_display_mode !== 'none'"
        hydrate-on-visible
        :tournament="tournament"
      />

      <UiCard :title="$t('Location')">
        <template #content>
          <UiCardContent>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <address
                  v-if="tournament.address"
                  class="text-subtitle2"
                >
                  <ul>
                    <li>{{ tournament.address.street }}</li>
                    <li>{{ tournament.address.postalCode }} {{ tournament.address.city }}</li>
                    <li>
                      {{ tournament.address.province }}
                    </li>
                    <li>{{ tournament.address.countryCode }}</li>
                  </ul>
                </address>
              </div>
              <div>
                <iframe
                  height="250"
                  style="border:0"
                  loading="lazy"
                  class="rounded-lg overflow-hidden w-full"
                  referrerpolicy="no-referrer-when-downgrade"
                  :src="googleSrc"
                />
              </div>
            </div>
          </UiCardContent>
        </template>
      </UiCard>

      <LazyVisibleTournamentRelatedTournamentsCard
        hydrate-on-visible
        :tournament="tournament"
      />
    </div>
  </div>
</template>
